import { useState, useEffect } from "react";
import { Layout, Menu, Typography, Avatar, Dropdown, Space } from "antd";
import {
  DashboardOutlined,
  MobileOutlined,
  AudioOutlined,
  UserOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useLogout } from "../hooks/useAuth";

const { Sider, Content, Header } = Layout;

const menuItems = [
  { key: "/", icon: <DashboardOutlined />, label: "总览" },
  {
    key: "device-group",
    icon: <MobileOutlined />,
    label: "设备管理",
    children: [
      { key: "/devices", label: "设备列表" },
      { key: "/devices/profiles", label: "型号管理" },
      { key: "/devices/provision", label: "烧录管理" },
    ],
  },
  {
    key: "simulator-group",
    icon: <AudioOutlined />,
    label: "模拟器",
    children: [
      { key: "/simulator/voice", label: "语音模拟" },
      { key: "/simulator/devices", label: "设备模拟" },
    ],
  },
];

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useLogout();

  const getSelectedKey = (path: string): string => {
    // 收集所有叶子节点的 key
    const collectKeys = (items: typeof menuItems): string[] => {
      const keys: string[] = [];
      for (const item of items) {
        if ("children" in item && item.children) {
          keys.push(...collectKeys(item.children as typeof menuItems));
        } else if (item.key) {
          keys.push(item.key);
        }
      }
      return keys;
    };
    const keys = collectKeys(menuItems);
    // 优先精确匹配
    if (keys.includes(path)) return path;
    // 按 startsWith 匹配，选最长的前缀（如 /devices/provision 不会误配 /devices）
    const matches = keys.filter((k) => k !== "/" && path.startsWith(k));
    if (matches.length > 0) {
      matches.sort((a, b) => b.length - a.length);
      return matches[0];
    }
    return "/";
  };
  const selectedKey = getSelectedKey(location.pathname);

  // 受控的展开状态，支持用户点击切换
  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>(() => {
    const keys: string[] = [];
    if (location.pathname.startsWith("/simulator"))
      keys.push("simulator-group");
    if (location.pathname.startsWith("/devices/provision"))
      keys.push("device-group");
    return keys;
  });
  useEffect(() => {
    setMenuOpenKeys((prev) => {
      const needed = new Set(prev);
      if (location.pathname.startsWith("/simulator"))
        needed.add("simulator-group");
      if (location.pathname.startsWith("/devices/provision"))
        needed.add("device-group");
      return Array.from(needed);
    });
  }, [location.pathname]);

  const userMenuItems = isAuthenticated
    ? [
        {
          key: "profile",
          icon: <UserOutlined />,
          label: user?.display_name || "用户",
          disabled: true,
        },
        { type: "divider" as const },
        {
          key: "logout",
          icon: <LogoutOutlined />,
          label: "退出登录",
          danger: true,
        },
      ]
    : [
        {
          key: "login",
          icon: <UserOutlined />,
          label: "登录",
        },
      ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        theme="dark"
        style={{ position: "fixed", height: "100vh", left: 0, zIndex: 100 }}
        width={220}
      >
        <div
          style={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Typography.Text
            strong
            style={{ color: "#fff", fontSize: 16, letterSpacing: 1 }}
          >
            🤖 AI IoT
          </Typography.Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={menuOpenKeys}
          onOpenChange={setMenuOpenKeys}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0, marginTop: 8 }}
        />
      </Sider>
      <Layout style={{ marginLeft: 220 }}>
        <Header
          style={{
            background: "#fff",
            padding: "0 24px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            AI IoT 智能家居平台
          </Typography.Text>

          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: ({ key }) => {
                if (key === "logout") logout();
                if (key === "login") navigate("/login");
              },
            }}
            placement="bottomRight"
          >
            <Space style={{ cursor: "pointer" }}>
              <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{
                  backgroundColor: isAuthenticated ? "#1677ff" : "#d9d9d9",
                }}
              />
              <Typography.Text>
                {isAuthenticated ? user?.display_name || "用户" : "未登录"}
              </Typography.Text>
            </Space>
          </Dropdown>
        </Header>
        <Content
          style={{
            padding: 24,
            background: "#f5f5f5",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
